import { useRef, useState, useEffect } from 'react'

const useGridLayout = () => {
  const gridRef = useRef<HTMLDivElement>(null)
  // The grid element in state too, so what watches it follows it to a new grid (see below). attachGrid is its ref.
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null)
  const attachGrid = (element: HTMLDivElement | null): void => {
    gridRef.current = element
    setGridElement(element)
  }
  const [gridWidth, setGridWidth] = useState(1)
  // Initialize to 0 so that effectiveGridHeight falls back to window.innerHeight
  // until the actual measurement is available
  const [gridHeight, setGridHeight] = useState(0)

  // Shared function to update dimensions from the grid element
  const updateDimensions = () => {
    if (gridRef.current) {
      const { clientWidth, clientHeight } = gridRef.current
      // Only update if values actually changed to avoid unnecessary re-renders
      setGridWidth((prev) => (prev !== clientWidth ? clientWidth : prev))
      setGridHeight((prev) => (prev !== clientHeight ? clientHeight : prev))
    }
  }

  // Debounced update to handle cases where multiple rapid changes occur
  const debouncedUpdateRef = useRef<number | null>(null)
  const scheduleUpdate = () => {
    if (debouncedUpdateRef.current) {
      cancelAnimationFrame(debouncedUpdateRef.current)
    }
    debouncedUpdateRef.current = requestAnimationFrame(() => {
      updateDimensions()
      debouncedUpdateRef.current = null
    })
  }

  // Memoize the callback to prevent recreating ResizeObserver on every render
  const observerCallback: ResizeObserverCallback = (entries: ResizeObserverEntry[]) => {
    window.requestAnimationFrame((): void | undefined => {
      if (!Array.isArray(entries) || !entries.length) {
        return
      }
      updateDimensions()
    })
  }

  // Watch the grid and the elements around it while it's mounted. A form, chart, calendar or Custom View replaces
  // the grid, and switching back mounts a new one, so the observers follow the element rather than the first grid.
  useEffect(() => {
    if (!gridElement) return
    updateDimensions()

    const resizeObserver = new ResizeObserver((entries, observer) => {
      observerCallback(entries, observer)
    })
    resizeObserver.observe(gridElement)

    // Observe ancestor elements to detect when container height changes
    // (e.g., when the spreadsheet is embedded in a document and resized)
    // We traverse up to 5 levels to cover typical embedding scenarios including
    // EmbeddedDashboard -> Spreadsheet -> DataContextProvider -> SpreadsheetComponent -> spreadsheet div -> data-view
    const ancestors: HTMLElement[] = []
    let ancestor = gridElement.parentElement
    for (let i = 0; i < 5 && ancestor; i++) {
      resizeObserver.observe(ancestor)
      ancestors.push(ancestor)
      ancestor = ancestor.parentElement
    }

    // Also use MutationObserver to detect style attribute changes on ancestors
    // This catches cases where height is changed via inline styles (e.g., EmbeddedDashboard)
    // We track the last known height of each ancestor to avoid unnecessary updates
    const ancestorHeights = new Map<HTMLElement, number>(ancestors.map((el) => [el, el.clientHeight]))

    const mutationObserver = new MutationObserver(() => {
      // Only trigger update if an ancestor's height actually changed
      let heightChanged = false
      for (const [el, lastHeight] of ancestorHeights) {
        const currentHeight = el.clientHeight
        if (currentHeight !== lastHeight) {
          ancestorHeights.set(el, currentHeight)
          heightChanged = true
        }
      }
      if (heightChanged) {
        scheduleUpdate()
      }
    })

    ancestors.forEach((el) => {
      mutationObserver.observe(el, {
        attributes: true,
        attributeFilter: ['style']
      })
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [gridElement])

  useEffect(() => {
    return () => {
      if (debouncedUpdateRef.current) {
        cancelAnimationFrame(debouncedUpdateRef.current)
      }
    }
  }, [])

  // Listen for window resize events to handle browser zoom changes
  // ResizeObserver doesn't fire on zoom because CSS pixel dimensions don't change
  useEffect(() => {
    const handleWindowResize = () => {
      window.requestAnimationFrame(updateDimensions)
    }

    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [updateDimensions])

  return { gridRef, gridElement, attachGrid, gridWidth, gridHeight, updateDimensions }
}

export default useGridLayout
